import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useProjectStore } from "./useProjectStore";
import { toast } from "./useToastStore";

/**
 * Tell the person a mutation failed, in the server's words when it sent any.
 * Every write route answers a refusal with { error, code } (writeRefusal in
 * admin-api/middleware/validateProject.js), e.g. "You are not a member of the
 * current project." Before this, every failure here went to the console only,
 * and the person saw nothing happen.
 */
async function surfaceFailure(res, action) {
  let message = null;
  try {
    const body = await res.json();
    message = body?.error ?? null;
  } catch {
    // not JSON — fall through to the generic line
  }
  toast.error(message || `${action} failed (${res.status}).`);
}

export const useAoiStore = create((set, get) => ({

  // STATE
  aois: [],
  activeAoiId: null,      // AoiPanel domain — which AOI is being edited/drawn
  selectedAoiId: null,    // LayerPanel domain — which AOI is pinned in Active Layers / visible on map
  hoveredAoiId: null,
  dirtyAoiId: null,
  loading: false,
  imageryCoverageByAoi: {},
  imageryCoverageLoading: {},

  // Day 9 — polygon (vertex) edit mode. When set, MapView puts MlDraw
  // into vertexEditMode after loading the active AOI's polygon. Distinct
  // from activeAoiId because selection ≠ edit-polygon: row click selects
  // (and zooms), the Edit Polygon icon (or AoiToolbar Vertex button)
  // enters polygon edit. Always implies activeAoiId === aoiPolygonEditMode.
  aoiPolygonEditMode: null,

  // Day 9 (Path A revision) — transient flag set by AoisTab's "+ New" /
  // "Manage" buttons and consumed by AoiPanel on visibility transition.
  // 'new'    → AoiPanel focuses + highlights the name input (~2s flash)
  // 'manage' → AoiPanel renders normally, no input emphasis
  // null     → no recent entry; AoiPanel does nothing special
  aoiEditorEntryIntent: null,

  //
  // INTERNAL HELPERS
  //
  getActiveProjectId: () => {
    const { activeProjectId, projects } = useProjectStore.getState();
    const project = projects.find(
      p => p.slug === activeProjectId || p.id === activeProjectId
    );
    return project?.id || null;
  },

  //
  // LOAD AOIs (PROJECT SCOPED)
  //
  loadAois: async () => {
    const projectId = get().getActiveProjectId();
    if (!projectId) {
      set({ aois: [], activeAoiId: null });
      return;
    }

    set({ loading: true });

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois`
      );

      if (!res.ok) throw new Error(res.status);

      const data = await res.json();

      set({
        aois: Array.isArray(data) ? data : [],
        loading: false
      });

    } catch (err) {
      console.error("AOI load failed", err);
      set({ loading: false });
    }
  },

  //
  // CREATE AOI
  //
  createAoi: async ({ name, description, geom }) => {
    const projectId = get().getActiveProjectId();
    if (!projectId) return null;

    // Normalize geometry if provided
    let geometry = geom;

    if (geom?.type === "Feature") {
      geometry = geom.geometry;
    }

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId,
            name,
            description,
            geom: geometry ?? null,
            props: {}
          })
        }
      );

      if (!res.ok) {
        await surfaceFailure(res, "Creating the AOI");
        return null;
      }

      const created = await res.json();

      console.log('[createAoi] response:', created);
      console.log('[createAoi] aois before:', get().aois.length);

      set(state => ({
        aois: [...state.aois, created],
        activeAoiId: created.id,
        dirtyAoiId: null
      }));
      console.log('[createAoi] aois after:', get().aois.length);

      return created;

      console.log('[createAoi] response from server:', created);
      console.log('[createAoi] projectId we sent:', projectId);


    } catch (err) {
      console.error("Create AOI error", err);
      return null;
    }
  },

  // AoiPanel domain — sets which AOI is being edited. Use selectAoiForMap for LayerPanel visibility.
  selectAoi: (id) => get().setActiveAoi(id),

  //
  // UPDATE META
  //
  updateAoi: async (id, updates) => {
    const projectId = get().getActiveProjectId();
    if (!projectId || !id) return;

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois/${id}/meta`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates)
        }
      );

      if (!res.ok) {
        await surfaceFailure(res, "Saving the AOI");
        return;
      }

      const updated = await res.json();

      set(state => ({
        aois: state.aois.map(a =>
          a.id === id ? updated : a
        )
      }));

      return updated;

    } catch (err) {
      console.error("Update AOI meta failed", err);
    }
  },

  //
  // UPDATE GEOMETRY
  //
  updateAoiGeometry: async (id, input) => {
    const projectId = get().getActiveProjectId();
    if (!projectId || !id || !input) return;

    let geom = input?.type === "Feature"
      ? input.geometry
      : input;

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois/${id}/geometry`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ geom })
        }
      );

      if (!res.ok) {
        await surfaceFailure(res, "Saving the AOI geometry");
        return;
      }

      const updated = await res.json();

      set(state => {
        const nextCoverage = { ...state.imageryCoverageByAoi };
        delete nextCoverage[id];

        return {
          aois: state.aois.map(a =>
            a.id === id ? updated : a
          ),
          dirtyAoiId: null,
          imageryCoverageByAoi: nextCoverage
        };
      });

      // re-fetch coverage
      await get().loadImageryCoverage(id);

      return updated;

    } catch (err) {
      console.error("Update AOI geometry failed", err);
    }
  },

  //
  // DELETE
  //
  deleteAoi: async (id) => {
    const projectId = get().getActiveProjectId();
    if (!projectId || !id) return;

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois/${id}`,
        { method: "DELETE" }
      );

      // The AOI leaves the list only when the server says it is gone. This
      // used to remove it regardless, so a refused delete looked like a
      // successful one until the next reload brought the AOI back.
      if (!res.ok) {
        await surfaceFailure(res, "Deleting the AOI");
        return;
      }

      set(state => ({
        aois: state.aois.filter(a => a.id !== id),
        activeAoiId: state.activeAoiId === id ? null : state.activeAoiId
      }));

    } catch (err) {
      console.error("Delete AOI failed", err);
    }
  },

  //
  // IMAGERY COVERAGE
  //
  loadImageryCoverage: async (aoiId) => {
    if (!aoiId) return;

    const { imageryCoverageByAoi, imageryCoverageLoading } = get();

    if (imageryCoverageByAoi[aoiId] || imageryCoverageLoading[aoiId]) {
      return;
    }

    set(state => ({
      imageryCoverageLoading: {
        ...state.imageryCoverageLoading,
        [aoiId]: true
      }
    }));

    try {
      const res = await fetchWithAuth(
        `/api/v1/aois/${aoiId}/imagery-coverage`
      );

      if (!res.ok) return;

      const json = await res.json();
      const rows = json.coverage ?? [];

      // ---- DATASET SELECTION LOGIC ----

      // Only allow ≥ 95% coverage
      const eligible = rows
        .filter(r => r.coverage_ratio >= 0.95)
        .sort((a, b) => a.resolution_cm - b.resolution_cm); // lower cm = higher resolution

      const selected = eligible[0] ?? null;

      set(state => ({
        imageryCoverageByAoi: {
          ...state.imageryCoverageByAoi,
          [aoiId]: {
            datasets: rows,
            selectedDatasetId: selected?.dataset_id ?? null,
            isEligible: !!selected
          }
        }
      }));

    } catch (err) {
      console.error("Imagery coverage failed", err);
    } finally {
      set(state => ({
        imageryCoverageLoading: {
          ...state.imageryCoverageLoading,
          [aoiId]: false
        }
      }));
    }
  },

  //
  // UI STATE
  //
  setActiveAoi: id => set({ activeAoiId: id }),
  setHoveredAoi: id => set({ hoveredAoiId: id }),
  setDirtyAoi: id => set({ dirtyAoiId: id }),
  clearDirtyAoi: () => set({ dirtyAoiId: null }),
  // Direct setter for snapshot restore — never toggles
  setSelectedAoiForMap: (id) => set({ selectedAoiId: id }),

  // Day 9 — polygon (vertex) edit mode. Pre-condition: aoiId must already
  // be the active AOI (or this also sets it). MapView's effect subscribes
  // to aoiPolygonEditMode and flips MlDraw into vertexEditMode when set.
  // Caller is responsible for ensuring the AOI has a geom (Edit Polygon
  // affordance is disabled for NULL-geom drafts upstream in AoiPanel).
  enterAoiPolygonEdit: (aoiId) => set({
    activeAoiId: aoiId,
    aoiPolygonEditMode: aoiId,
  }),
  exitAoiPolygonEdit: () => set({ aoiPolygonEditMode: null }),

  // Path A — editor entry intent. AoisTab sets via "+ New" / "Manage"
  // buttons. AoiPanel consumes on visibility transition and immediately
  // clears the flag so it's a one-shot.
  setEditorEntryIntent: (intent) => set({ aoiEditorEntryIntent: intent }),
  clearEditorEntryIntent: () => set({ aoiEditorEntryIntent: null }),

  // LayerPanel domain — toggles map visibility selection independently of editing state
  selectAoiForMap: (id) => set(state => ({
    selectedAoiId: state.selectedAoiId === id ? null : id
  })),

  //
  // RESET ON PROJECT CHANGE
  //
  resetForProjectChange: () => {
    set({
      aois: [],
      activeAoiId: null,
      selectedAoiId: null,
      dirtyAoiId: null,
      aoiPolygonEditMode: null,
      aoiEditorEntryIntent: null,
      imageryCoverageByAoi: {},
      imageryCoverageLoading: {}
    });
  }

}));